import { eyeIcon } from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";

import { saveDebugState } from "./DebugCanvas";

// Flutter icon
const FlutterMenuIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <polygon points="3,14 9,8 13,8 7,14" fill="#54C5F8" />
    <polygon points="7,14 13,8 17,8 13,14 17,20 13,20" fill="#01579B" />
    <polygon points="13,14 17,20 13,20 10,17" fill="#29B6F6" />
  </svg>
);

// pub.dev icon
const PubDevMenuIcon = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

// Material Design icon
const MaterialMenuIcon = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v9l4 4" />
  </svg>
);

// Widget catalog icon
const WidgetMenuIcon = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </svg>
);

// Dart icon (language)
const DartMenuIcon = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
}> = React.memo((props) => {
  return (
    <MainMenu>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />

      {/* Flutter Resource Links */}
      <MainMenu.Separator />
      <MainMenu.ItemLink
        icon={
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 21V9" />
          </svg>
        }
        href="/flutter-templates"
        target="_blank"
      >
        📋 Flutter Diagram Templates
      </MainMenu.ItemLink>
      <MainMenu.ItemLink
        icon={FlutterMenuIcon}
        href="https://docs.flutter.dev"
        target="_blank"
      >
        Flutter Docs
      </MainMenu.ItemLink>
      <MainMenu.ItemLink
        icon={PubDevMenuIcon}
        href="https://pub.dev"
        target="_blank"
      >
        pub.dev — Package Registry
      </MainMenu.ItemLink>
      <MainMenu.ItemLink
        icon={MaterialMenuIcon}
        href="https://m3.material.io"
        target="_blank"
      >
        Material 3 Design Guide
      </MainMenu.ItemLink>
      <MainMenu.ItemLink
        icon={WidgetMenuIcon}
        href="https://docs.flutter.dev/reference/widgets"
        target="_blank"
      >
        Flutter Widget Catalog
      </MainMenu.ItemLink>
      <MainMenu.ItemLink
        icon={DartMenuIcon}
        href="https://dart.dev/guides"
        target="_blank"
      >
        Dart Language Guide
      </MainMenu.ItemLink>

      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />

      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
    </MainMenu>
  );
});
